import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { PostsApi } from '@/services/api'; // Import PostsApi

interface AuthenticatedImageProps {
  filename: string | null | undefined;
  alt: string;
  className?: string;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ filename, alt, className }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let currentObjectUrl: string | null = null;
    setError(null);
    setObjectUrl(null); // Clear previous image

    if (filename) {
      setIsLoading(true);

      const fetchBlob = async () => {
        try {
          const blob = await PostsApi.getImageBlob(filename); // Use the API method
          currentObjectUrl = URL.createObjectURL(blob);
          setObjectUrl(currentObjectUrl);
          setError(null);
        } catch (err) {
          console.error(`Error fetching image blob for ${filename}:`, err);
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setObjectUrl(null);
        } finally {
          setIsLoading(false);
        }
      };

      fetchBlob();
    } else {
        setIsLoading(false); // No filename, not loading
    }

    // Cleanup function
    return () => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [filename]); // Re-run when filename changes

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center text-muted-foreground ${className}`}>
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center text-red-600 ${className}`}>
        <AlertTriangle className="h-5 w-5 mr-2" />
        <span className="text-xs">Preview Unavailable</span>
        {/* <span className="text-xs">Error: {error}</span> */}
      </div>
    );
  }

  if (objectUrl) {
    return (
      <img
        src={objectUrl}
        alt={alt}
        className={className}
        onError={() => {
            console.error(`Failed to load image from object URL: ${objectUrl}`);
            setError('Browser failed to display cached image.');
            setObjectUrl(null);
        }}
      />
    );
  }

  return null; // Render nothing if no filename, not loading, and no error
};

export default AuthenticatedImage; 